import { Email } from "features/Resources/Email/models";
import { Task } from "features/Resources/Task/models";
import { Message } from "features/Resources/Message/models";
import { Meeting } from "features/Resources/Meeting/models";
import { ActionSummary, ActionSummaryData, ActionType, ActionV2 } from "../models";
import { extractAction, fromActionPrefixId } from ".";

interface NormalizedSendAt {
  label: string;
  dateValue: Date | undefined;
  dependencyId: string | undefined;
  actionType: ActionType;
  triggerType: "timing" | "dependency";
  isImmediate?: boolean;
}

const sendAtMap = {
  email: (action: ActionV2 | ActionSummaryData): NormalizedSendAt => {
    const email = action as Email;
    return {
      label: email?.deliverAfterDependencyId ? "After" : email.deliveredAt ? "Sent" : "At",
      dateValue: email?.deliveredAt ?? email.deliverAt,
      dependencyId: email?.deliverAfterDependencyId,
      actionType: "email",
      triggerType: email?.deliverAfterDependencyId ? "dependency" : "timing",
      isImmediate: email?.deliverImmediately,
    };
  },
  message: (action: ActionV2 | ActionSummaryData): NormalizedSendAt => {
    const message = action as Message;
    return {
      label: message?.deliverAfterDependencyId ? "After" : message.deliveredAt ? "Sent" : "At",
      dateValue: message?.deliveredAt ?? message.deliverAt,
      dependencyId: message?.deliverAfterDependencyId,
      actionType: "message",
      triggerType: message?.deliverAfterDependencyId ? "dependency" : "timing",
      isImmediate: message?.deliverImmediately,
    };
  },
  task: (action: ActionV2 | ActionSummaryData): NormalizedSendAt => {
    const task = action as Task;
    return {
      label: task?.startAfterDependencyId ? "After" : task.startedAt ? "Started" : "Starts",
      dateValue: task.startedAt ?? task?.startAt,
      dependencyId: task?.startAfterDependencyId,
      actionType: "task",
      triggerType: task?.startAfterDependencyId ? "dependency" : "timing",
      isImmediate: task?.startImmediately,
    };
  },
  meeting: (action: ActionV2 | ActionSummaryData): NormalizedSendAt => {
    const meeting = action as Meeting;
    return {
      label: meeting.scheduledAt ? "Scheduled" : "Proposed",
      dateValue: meeting.scheduledAt ?? meeting?.proposedAt,
      dependencyId: undefined,
      actionType: "meeting",
      triggerType: "timing",
      isImmediate: false,
    };
  },
  checkpoint: (action: ActionV2 | ActionSummaryData): NormalizedSendAt => {
    const checkpoint = action as any;
    return {
      label: checkpoint.evaluatedAt ? "Evaluated" : "At",
      dateValue: checkpoint?.evaluatedAt ?? checkpoint.evaluateAt,
      dependencyId: undefined,
      actionType: "checkpoint",
      triggerType: "timing",
      isImmediate: false,
    };
  },
} satisfies Record<ActionType, (action: ActionV2) => NormalizedSendAt>;

export default function normalizeSendAt(action: ActionV2 | ActionSummary): NormalizedSendAt | Record<string, never> {
  const resolvedAction = extractAction(action);
  const { resourceType } = fromActionPrefixId(resolvedAction?.prefixId);

  if (!resourceType) {
    // if no action is given, fail softly
    return {};
  }

  const normalizer = sendAtMap[resourceType];

  if (!normalizer) {
    throw new Error(`No normalizer for resourceType: ${resourceType}`);
  }

  return normalizer(resolvedAction as ActionV2);
}
